/**
 * TypeScript mirrors of the Billing V2 types shipped in
 * springboot-shared-library and professorx (Stream A + B), per
 * payments-redesign.md §5 + §F11.
 *
 * Every shape here has a Kotlin counterpart in the
 * `com.xconvert.springbootsharedlibrary.billing` package; the
 * contract-fixture round-trip test under `__fixtures__/contract`
 * + `billing.test.ts` enforces the on-the-wire shapes stay aligned.
 *
 * **String enums.** All enums use string-literal unions matching the
 * Kotlin enum's `.name()` form. The Mongo POJO codec serializes Kotlin
 * enums by name, so the on-the-wire string values are fixed by the
 * Kotlin enum declaration order — renaming a Kotlin enum value
 * silently invalidates persisted documents AND breaks this TypeScript
 * mirror.
 *
 * **`ObjectId` is `string` (24-char hex)** in the on-the-wire shape.
 * Kotlin's `org.bson.types.ObjectId` serializes to the hex form when
 * controllers call `.toHexString()` (which they do everywhere — see
 * `BillingController` for the pattern).
 *
 * **`Instant` is `string` (ISO-8601)** in the on-the-wire shape.
 * Kotlin's `java.time.Instant` round-trips cleanly through ISO-8601;
 * the controllers call `.toString()`.
 *
 * **The `metadata` field is opaque.** A few types carry a free-form
 * `Document` field for gateway-specific extras (Stripe pause flags,
 * admin grant context, drift-detection details). Modeled as
 * `Record<string, unknown>` here — callers should not introspect it
 * structurally.
 *
 * See payments-redesign.md §5.
 */
// ===========================================================================
// Enums (string-literal unions)
// ===========================================================================
export const CreditPool = {
    API_SUBSCRIPTION_MONTHLY: 'API_SUBSCRIPTION_MONTHLY',
    API_PAYG: 'API_PAYG',
};
export const EntitlementType = {
    WEBSITE: 'WEBSITE',
    API: 'API',
};
export const EntitlementState = {
    ACTIVE: 'ACTIVE',
    PAUSED: 'PAUSED',
    PAST_DUE: 'PAST_DUE',
    CANCELED_ACCESS_UNTIL_PERIOD_END: 'CANCELED_ACCESS_UNTIL_PERIOD_END',
    EXPIRED: 'EXPIRED',
};
export const SubscriptionStatus = {
    ACTIVE: 'ACTIVE',
    PAST_DUE: 'PAST_DUE',
    PAUSED: 'PAUSED',
    CANCELED: 'CANCELED',
    EXPIRED: 'EXPIRED',
    ORPHAN: 'ORPHAN',
};
export const PaymentEventType = {
    SUBSCRIPTION_CREATED: 'SUBSCRIPTION_CREATED',
    SUBSCRIPTION_RENEWED: 'SUBSCRIPTION_RENEWED',
    SUBSCRIPTION_CANCELED: 'SUBSCRIPTION_CANCELED',
    SUBSCRIPTION_STATE_CHANGED: 'SUBSCRIPTION_STATE_CHANGED',
    PAYMENT_SUCCEEDED: 'PAYMENT_SUCCEEDED',
    PAYMENT_FAILED: 'PAYMENT_FAILED',
    REFUND_ISSUED: 'REFUND_ISSUED',
    DISPUTE_CREATED: 'DISPUTE_CREATED',
    CREDIT_TOPUP_PURCHASED: 'CREDIT_TOPUP_PURCHASED',
    CREDIT_GRANTED: 'CREDIT_GRANTED',
    CREDIT_EXPIRED: 'CREDIT_EXPIRED',
    ADMIN_ENTITLEMENT_GRANTED: 'ADMIN_ENTITLEMENT_GRANTED',
    ADMIN_ENTITLEMENT_REVOKED: 'ADMIN_ENTITLEMENT_REVOKED',
    /** Stripe checkout session abandoned (user closed tab pre-payment). */
    CHECKOUT_SESSION_EXPIRED: 'CHECKOUT_SESSION_EXPIRED',
};
export const PaymentEventActor = {
    USER: 'USER',
    WEBHOOK: 'WEBHOOK',
    ADMIN: 'ADMIN',
    CRON: 'CRON',
    BACKFILL: 'BACKFILL',
};
export const EntitlementSource = {
    STRIPE_SUBSCRIPTION: 'STRIPE_SUBSCRIPTION',
    PAYPAL_SUBSCRIPTION: 'PAYPAL_SUBSCRIPTION',
    API_TOPUP: 'API_TOPUP',
    ADMIN_GRANT: 'ADMIN_GRANT',
    FREE_TIER: 'FREE_TIER',
};
export const Provider = {
    STRIPE: 'STRIPE',
    PAYPAL: 'PAYPAL',
};
export const GrantType = {
    PAID_TOPUP: 'PAID_TOPUP',
    SUBSCRIPTION_ALLOTMENT: 'SUBSCRIPTION_ALLOTMENT',
    ADMIN_PROMO: 'ADMIN_PROMO',
    REFUND_REVERSAL: 'REFUND_REVERSAL',
};
// ===========================================================================
// Topup + API subscription request/response shapes
// ===========================================================================
export const CreditPackId = {
    STARTER: 'STARTER',
    DEVELOPER: 'DEVELOPER',
    PRO: 'PRO',
    TEAM: 'TEAM',
};
export const ApiSubscriptionPlanId = {
    API_STARTER: 'API_STARTER',
    API_PRO: 'API_PRO',
    API_SCALE: 'API_SCALE',
};
